'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const UI_THEME_STORAGE_KEY = 'skoleom-ui-theme';

type UiTheme = 'dark' | 'light';
type UiThemeSource = 'system' | 'manual';

interface UiThemeContextValue {
  theme: UiTheme;
  ready: boolean;
  setTheme: (theme: UiTheme) => void;
  toggle: () => void;
}

const UiThemeContext = createContext<UiThemeContextValue | null>(null);

function getSystemTheme(): UiTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredTheme(): { theme: UiTheme; source: UiThemeSource } {
  if (typeof window === 'undefined') return { theme: 'dark', source: 'system' };
  const savedTheme = window.localStorage.getItem(UI_THEME_STORAGE_KEY);
  if (savedTheme === 'dark' || savedTheme === 'light') {
    return { theme: savedTheme, source: 'manual' };
  }
  return { theme: getSystemTheme(), source: 'system' };
}

export function useUiTheme(): UiThemeContextValue {
  const context = useContext(UiThemeContext);
  if (!context) throw new Error('useUiTheme doit être utilisé dans <UiThemeProvider>');
  return context;
}

export function UiThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<UiTheme>('dark');
  const [source, setSource] = useState<UiThemeSource>('system');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const initial = readStoredTheme();
    setThemeState(initial.theme);
    setSource(initial.source);
    setReady(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || source !== 'system') return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) =>
      setThemeState(event.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [source]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.uiTheme = theme;
    if (source === 'manual') window.localStorage.setItem(UI_THEME_STORAGE_KEY, theme);
    else window.localStorage.removeItem(UI_THEME_STORAGE_KEY);
  }, [source, theme]);

  const setTheme = useCallback((nextTheme: UiTheme) => {
    setSource('manual');
    setThemeState(nextTheme);
  }, []);

  const toggle = useCallback(() => {
    setSource('manual');
    setThemeState((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(
    () => ({ theme, ready, setTheme, toggle }),
    [ready, setTheme, theme, toggle],
  );

  return <UiThemeContext.Provider value={value}>{children}</UiThemeContext.Provider>;
}

export type { UiTheme };
